import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { jsx as _jsx } from "react/jsx-runtime";
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useIsInBottomSheet } from '../../providers';
import { useDevice } from '../../providers/Device';
import { Autocomplete } from '../Autocomplete';
import { getOptionLabel, getOptionSelected } from '../Autocomplete/AutocompleteSelectors';
import { useBiAction } from '../BiTracking';
import { FullRow } from '../Layout/FullRow';
import { MenuFooter } from '../Menu/MenuFooter';
import { menuFooterItemsFromOptions } from '../Menu/MenuFooter.utils';
import { Tooltip } from '../Tooltip';
import { Typography } from '../Typography';
import styles from './EnhancedAutocomplete.module.scss';
import { EnhancedAutocompleteTestIds, EnhancedAutocompleteOptionType, getOptions, mapDataToOption, findExactMatchOption, filterEnhancedOptions } from './EnhancedAutocomplete.utils';
export const EnhancedAutocomplete = (props)=>{
    var _ref;
    const { testId, applyOptionChange: _applyOptionChange, options: _options, menuWidth, emptySearchText, newItemOption, promotionalActionOption, isLoading, onRefresh, textFieldProps, className, style, selectedOption, disabled, itemRenderer, size, error, helperText, errorInfo, disabledItems, onInputChange, disableClientFilter, disableExactMatchOnType, disableSort, getFilterText, dividers, getSectionLabel, sectionOrder, fullHeightSheet, mobile, listboxMaxHeight, menuStyleOptions, bi } = props;
    const logBiAction = useBiAction();
    const newItemOptions = useMemo(()=>{
        if (!newItemOption) return [];
        return Array.isArray(newItemOption) ? newItemOption : [
            newItemOption
        ];
    }, [
        newItemOption
    ]);
    const disabledItemsMap = useMemo(()=>{
        if (!disabledItems) return new Map();
        return new Map(disabledItems.map((item)=>[
                item.id,
                item.tooltip
            ]));
    }, [
        disabledItems
    ]);
    const externalCurrentOption = useMemo(()=>{
        return selectedOption ? mapDataToOption(selectedOption, props.keyField, props.labelField, getSectionLabel) : null;
    }, [
        selectedOption,
        props.keyField,
        props.labelField,
        getSectionLabel
    ]);
    const [managedCurrentOption, setManagedCurrentOption] = useState(externalCurrentOption);
    const [showAllOptions, setShowAllOptions] = useState(false);
    useEffect(()=>{
        if ((externalCurrentOption === null || externalCurrentOption === void 0 ? void 0 : externalCurrentOption.type) === EnhancedAutocompleteOptionType.Item) {
            setManagedCurrentOption(externalCurrentOption);
        }
    }, [
        externalCurrentOption
    ]);
    const options = useMemo(()=>{
        return getOptions(_options, props.keyField, props.labelField, getSectionLabel, sectionOrder, disableSort);
    }, [
        _options,
        props.keyField,
        props.labelField,
        getSectionLabel,
        sectionOrder,
        disableSort
    ]);
    const value = managedCurrentOption;
    const valueLabel = (_ref = value === null || value === void 0 ? void 0 : value.label) !== null && _ref !== void 0 ? _ref : '';
    const { isMobileDevice } = useDevice();
    const isInBottomSheet = useIsInBottomSheet();
    const usesBottomSheetSearch = isInBottomSheet || isMobileDevice;
    // WHY: desktop MUI erases the query when it equals the selection label, so '' must fall back to valueLabel; the bottom-sheet search passes '' only for a genuinely empty query, where falling back would hide every non-selected option.
    const emptyQueryFallback = usesBottomSheetSearch ? '' : valueLabel;
    const applyOptionChange = useCallback((newOption)=>{
        if ((bi === null || bi === void 0 ? void 0 : bi.name) && (newOption === null || newOption === void 0 ? void 0 : newOption.type) === EnhancedAutocompleteOptionType.Item) {
            logBiAction({
                entityType: 'enhanced_autocomplete',
                entityName: bi.name,
                actionType: 'select',
                data: _object_spread_props(_object_spread({}, bi.data), {
                    test_id: testId !== null && testId !== void 0 ? testId : '',
                    selected_value: newOption.label
                })
            });
        }
        setManagedCurrentOption(newOption);
        const item = newOption === null || newOption === void 0 ? void 0 : newOption.data;
        _applyOptionChange === null || _applyOptionChange === void 0 ? void 0 : _applyOptionChange(item);
    }, [
        bi,
        logBiAction,
        testId,
        _applyOptionChange
    ]);
    const runNewItemAction = async (option)=>{
        const newItem = await option.onClick();
        if (newItem) {
            await (onRefresh === null || onRefresh === void 0 ? void 0 : onRefresh());
            applyOptionChange(mapDataToOption(newItem, props.keyField, props.labelField, getSectionLabel));
        }
    };
    const footerItems = menuFooterItemsFromOptions({
        newItemOptions,
        promotionalAction: promotionalActionOption,
        testIds: {
            newItemOption: EnhancedAutocompleteTestIds.NewItemOption,
            promotionalAction: EnhancedAutocompleteTestIds.PromotionalActionOption()
        },
        runNewItemOption: (option)=>runNewItemAction(option),
        runPromotionalAction: (action)=>{
            var // WHY: mousedown inside the popover keeps input focus, so the dropdown would
            // stay open after the action fires; blur to dismiss it (matches selecting an option).
            _document_activeElement;
            (_document_activeElement = document.activeElement) === null || _document_activeElement === void 0 ? void 0 : _document_activeElement.blur();
            action.onClick();
        }
    });
    const handleTabAutoSelect = useCallback((event)=>{
        if (event.key !== 'Tab' || !valueLabel) return;
        const matches = filterEnhancedOptions(options, valueLabel, getFilterText).filter((option)=>option.type === EnhancedAutocompleteOptionType.Item && !disabledItemsMap.has(option.value));
        if (matches.length === 1) {
            applyOptionChange(matches[0]);
        }
    }, [
        valueLabel,
        options,
        getFilterText,
        disabledItemsMap,
        applyOptionChange
    ]);
    return /*#__PURE__*/ _jsx(Autocomplete, {
        onKeyDown: handleTabAutoSelect,
        // eslint-disable-next-line custom/no-hardcoded-nobi-in-seagull -- EnhancedAutocomplete self-logs the select event in applyOptionChange; the inner base Autocomplete must stay silent to avoid a double-fire
        noBi: true,
        id: testId !== null && testId !== void 0 ? testId : EnhancedAutocompleteTestIds.EnhancedAutocomplete(),
        error: !!error,
        helperText: helperText,
        errorInfo: errorInfo,
        disabled: disabled,
        selectOnFocus: true,
        className: className,
        style: style,
        size: size || 'small',
        menuWidth: menuWidth,
        listboxMaxHeight: listboxMaxHeight,
        textFieldProps: textFieldProps,
        forcePopupIcon: true,
        testId: testId !== null && testId !== void 0 ? testId : EnhancedAutocompleteTestIds.EnhancedAutocomplete(),
        loading: isLoading,
        options: options,
        dividers: dividers,
        fullHeightSheet: fullHeightSheet,
        mobile: mobile,
        clearOnEscape: true,
        popoverFooter: footerItems.length > 0 && /*#__PURE__*/ _jsx(MenuFooter, {
            items: footerItems,
            closeBottomSheetOnAction: true
        }),
        renderOption: (option)=>{
            if ((option === null || option === void 0 ? void 0 : option.type) === EnhancedAutocompleteOptionType.EmptySearch) {
                return /*#__PURE__*/ _jsx(Typography, {
                    as: "p",
                    testId: EnhancedAutocompleteTestIds.EmptySearchOption(),
                    children: option.label
                });
            }
            if ((option === null || option === void 0 ? void 0 : option.type) === EnhancedAutocompleteOptionType.SectionHeader) {
                return /*#__PURE__*/ _jsx(Typography, {
                    as: "p",
                    variant: menuStyleOptions ? 'paragraph1' : 'paragraph2',
                    weight: menuStyleOptions ? 'normal' : undefined,
                    skin: "secondary",
                    className: styles.sectionHeader,
                    testId: EnhancedAutocompleteTestIds.SectionHeader(option.label),
                    children: option.label
                });
            }
            const tooltip = disabledItemsMap.get(option.value);
            const content = /*#__PURE__*/ _jsx(FullRow, {
                className: menuStyleOptions ? styles.menuStyleOption : undefined,
                testId: EnhancedAutocompleteTestIds.EnhancedAutocompleteOption(option.value),
                children: itemRenderer ? itemRenderer(option.data) : /*#__PURE__*/ _jsx(Typography, {
                    as: "p",
                    ellipsis: true,
                    children: option.label
                })
            });
            return tooltip ? /*#__PURE__*/ _jsx(Tooltip, {
                title: tooltip,
                placement: "right",
                children: /*#__PURE__*/ _jsx("span", {
                    "data-testid": EnhancedAutocompleteTestIds.EnhancedAutocompleteOptionTooltip(option.value),
                    style: {
                        display: 'block',
                        width: '100%'
                    },
                    children: content
                })
            }) : content;
        },
        filterOptions: (options, state)=>{
            const NO_MATCHES_OPTION = {
                value: 'empty-search',
                label: emptySearchText,
                type: EnhancedAutocompleteOptionType.EmptySearch
            };
            const filteredOptions = showAllOptions || disableClientFilter ? options : filterEnhancedOptions(options, state.inputValue || emptyQueryFallback, getFilterText);
            return filteredOptions.length === 0 ? [
                NO_MATCHES_OPTION
            ] : filteredOptions;
        },
        value: value,
        inputValue: valueLabel,
        getOptionLabel: getOptionLabel,
        getOptionSelected: getOptionSelected,
        onBlur: ()=>{
            if ((managedCurrentOption === null || managedCurrentOption === void 0 ? void 0 : managedCurrentOption.type) !== EnhancedAutocompleteOptionType.Item) {
                applyOptionChange(null);
            }
        },
        onOpen: ()=>{
            setShowAllOptions(true);
        },
        onInputChange: (_, value, reason)=>{
            setShowAllOptions(false);
            if (reason === 'clear') {
                applyOptionChange(null);
                onInputChange === null || onInputChange === void 0 ? void 0 : onInputChange('');
                return;
            }
            if (reason !== 'input') return; // Other reasons are picked up by onChange handler.
            onInputChange === null || onInputChange === void 0 ? void 0 : onInputChange(value);
            const exactMatch = disableExactMatchOnType ? undefined : findExactMatchOption(options, value);
            applyOptionChange(exactMatch !== null && exactMatch !== void 0 ? exactMatch : {
                value: '',
                label: value,
                type: EnhancedAutocompleteOptionType.IncompleteSearch
            });
        },
        noOptionsText: emptySearchText,
        getOptionDisabled: (option)=>option.type === EnhancedAutocompleteOptionType.EmptySearch || option.type === EnhancedAutocompleteOptionType.SectionHeader || disabledItemsMap.has(option.value),
        onChange: async (event, value)=>{
            if (!value) {
                applyOptionChange(null);
                return;
            }
            const newValue = typeof value === 'string' ? {
                value: '',
                label: value,
                type: EnhancedAutocompleteOptionType.IncompleteSearch
            } : value;
            applyOptionChange(newValue);
        }
    });
};
